import { useState, useCallback, useEffect, useRef } from 'react';

import {
  addFacility,
  createTempFacility,
  finalizeFacility,
  patchFacility,
} from '../api/addFacilityApiService';
import { getFacility } from '../api/viewFacilityApiService';
import { addEquipment } from '../api/addEquipmentApiService';
import type { EquipmentListItem } from '../api/listEquipmentApiService';
import { editEquipment } from '../api/editEquipmentApiService';
import { getPresignedUrlsBatch } from '../api/presignedUrlBatchApiService';
import { uploadFileToS3 } from '../api/uploadFileToS3';
import {
  EQUIPMENT_ERROR_MESSAGES,
  FACILITY_CREATE_ERROR_MESSAGES,
} from '../errors';
import { FACILITY_LABELS } from '../constants/facilityLabels';
import { validateEntryFields } from './useAddEquipment';
import {
  validateNip,
  validateFacilityPhone as validatePhone,
  validateZip,
  formatNip,
} from '../utils/validation';
import {
  clearFacilitySession,
  getFacilitySession,
  setFacilitySession,
} from '../utils/facilitySessionStorage';
import { finalizeClient } from '@/epics/clientManagement/api/finalizeClientApiService';
import { deleteMediaResource } from '@/epics/clientManagement/api/deleteMediaResourceApiService';
import {
  useAddClientStore,
  type AddClientStoreState,
} from '@/epics/clientManagement/store/addClientStore';
import type {
  FacilityEntry,
  FacilityEntryErrors,
} from '../types/addFacilityType';
import type {
  EquipmentEntry,
  EquipmentEntryErrors,
  MediaResource,
} from '../types/addEquipmentType';

// ── Internal state types ─────────────────────────────────────────────────────

export interface ExistingFileMeta {
  filename: string;
  fileSize: number;
  fileType: string;
  path: string;
  resourceId: string;
  isUploaded: boolean;
}

export interface EditingEquipmentState {
  serverId: string;
  equipmentId: string;
  categoryLabel?: string;
  entry: EquipmentEntry;
  errors: EquipmentEntryErrors;
  uploadProgress: Record<string, number>;
  failedUploadFiles: string[];
  existingImageMeta: ExistingFileMeta | null;
  existingDocsMeta: ExistingFileMeta[];
  /** Paths of existing docs removed during editing — deleted from S3 on successful Save. */
  deletedDocPaths: string[];
}

interface EquipmentSubItem {
  entry: EquipmentEntry;
  errors: EquipmentEntryErrors;
  uploadProgress: Record<string, number>;
  failedUploadFiles: string[];
  /** Set after the equipment has been successfully POSTed to the API. */
  serverEquipmentId?: string | undefined;
}

interface FacilityItem {
  entry: FacilityEntry;
  errors: FacilityEntryErrors;
  equipment: EquipmentSubItem[];
  /**
   * UUID of the temp facility row in DB.
   * Null until the first "Dodaj aparaturę" is submitted for this facility slot.
   */
  tempFacilityId: string | null;
  /**
   * True if this facility is being edited (loaded from viewFacility API).
   * False for newly added facilities.
   */
  isEditMode?: boolean;
  /** State for in-place equipment editing (one at a time per facility). */
  editingEquipment?: EditingEquipmentState | undefined;
  /** Server-side status, loaded when in edit mode. */
  facilityStatus?: 'active' | 'inactive' | 'cascaded' | undefined;
  /** equipmentId → error message for serial-number conflicts surfaced by finalize. */
  equipmentConflicts?: Record<string, string>;
}

// ── Factory helpers ──────────────────────────────────────────────────────────

const createEmptyEquipmentEntry = (): EquipmentEntry => ({
  id: crypto.randomUUID(),
  name: '',
  description: '',
  category: '',
  serialNumber: '',
  dateOfProduction: undefined,
  modelName: '',
  docFiles: [],
  docResources: {},
});

const createEmptyEquipmentErrors = (): EquipmentEntryErrors => ({
  name: '',
  description: '',
  category: '',
  serialNumber: '',
  dateOfProduction: '',
  modelName: '',
  imageFile: '',
  docFiles: '',
});

const createEmptyEquipmentSubItem = (): EquipmentSubItem => ({
  entry: createEmptyEquipmentEntry(),
  errors: createEmptyEquipmentErrors(),
  uploadProgress: {},
  failedUploadFiles: [],
});

const createEmptyFacilityEntry = (): FacilityEntry => ({
  id: crypto.randomUUID(),
  name: '',
  nip: '',
  phone: '',
  countryCode: '+48',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: 'Polska',
});

const createEmptyFacilityErrors = (): FacilityEntryErrors => ({
  name: '',
  nip: '',
  phone: '',
  address1: '',
  city: '',
  state: '',
  zip: '',
});

const createEmptyFacilityItem = (): FacilityItem => ({
  entry: createEmptyFacilityEntry(),
  errors: createEmptyFacilityErrors(),
  equipment: [createEmptyEquipmentSubItem()],
  tempFacilityId: null,
});

// ── Hook ─────────────────────────────────────────────────────────────────────

interface UseAddFacilityOptions {
  mode?: 'add' | 'edit';
  editFacilityId?: string;
}

export const useAddFacility = (
  clientId?: string,
  options?: UseAddFacilityOptions
) => {
  const mode = options?.mode ?? 'add';
  const editFacilityId = options?.editFacilityId;
  const getStoredEquipmentServerIds = useAddClientStore(
    (s: AddClientStoreState) => s.getEquipmentServerIds
  );

  const [items, setItems] = useState<FacilityItem[]>(() => {
    if (clientId) {
      const savedIds = getFacilitySession(clientId);
      if (savedIds.length > 0) {
        return savedIds.map((tempFacilityId) => {
          const storedEquipIds = getStoredEquipmentServerIds(tempFacilityId);
          const equipment =
            storedEquipIds.length > 0
              ? storedEquipIds.map((serverEquipmentId: string | undefined) => ({
                  ...createEmptyEquipmentSubItem(),
                  serverEquipmentId,
                }))
              : [createEmptyEquipmentSubItem()];
          return {
            ...createEmptyFacilityItem(),
            entry: { ...createEmptyFacilityEntry(), id: tempFacilityId },
            tempFacilityId,
            equipment,
          };
        });
      }
    }
    return [createEmptyFacilityItem()];
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFacilitySubmitting, setIsFacilitySubmitting] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isEquipmentSuccessSnackbarOpen, setIsEquipmentSuccessSnackbarOpen] =
    useState(false);
  const [
    isEquipmentEditSuccessSnackbarOpen,
    setIsEquipmentEditSuccessSnackbarOpen,
  ] = useState(false);
  const [equipmentEditCount, setEquipmentEditCount] = useState(0);
  const [savedFacilityCount, setSavedFacilityCount] = useState(0);
  const [isLoading, setIsLoading] = useState(
    mode === 'edit' && !!editFacilityId
  );
  const [loadingFacilityId, setLoadingFacilityId] = useState<string | null>(
    null
  );

  // Tracks setInterval IDs for fake upload-progress animations on file select.
  // Key: `${facilityId}:${uploadKey}` e.g. "fac-1:image:scan.jpg"
  const fakeProgressTimers = useRef<
    Map<string, ReturnType<typeof setInterval>>
  >(new Map());

  const startFakeProgress = useCallback(
    (facilityId: string, key: string, fileSize: number) => {
      const timerKey = `${facilityId}:${key}`;
      const existing = fakeProgressTimers.current.get(timerKey);
      if (existing !== undefined) {
        clearInterval(existing);
      }

      const STEPS = 15;
      const DURATION_MS = 600;
      const stepMs = DURATION_MS / STEPS;
      let step = 0;

      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId && item.editingEquipment
            ? {
                ...item,
                editingEquipment: {
                  ...item.editingEquipment,
                  uploadProgress: {
                    ...item.editingEquipment.uploadProgress,
                    [key]: 0,
                  },
                },
              }
            : item
        )
      );

      const id = setInterval(() => {
        step += 1;
        const progress = Math.min((step / STEPS) * fileSize, fileSize);

        setItems((prev) =>
          prev.map((item) =>
            item.entry.id === facilityId && item.editingEquipment
              ? {
                  ...item,
                  editingEquipment: {
                    ...item.editingEquipment,
                    uploadProgress: {
                      ...item.editingEquipment.uploadProgress,
                      [key]: progress,
                    },
                  },
                }
              : item
          )
        );

        if (step >= STEPS) {
          clearInterval(id);
          fakeProgressTimers.current.delete(timerKey);
          setItems((prev) =>
            prev.map((item) => {
              if (item.entry.id !== facilityId || !item.editingEquipment) {
                return item;
              }
              const { [key]: _done, ...rest } =
                item.editingEquipment.uploadProgress;
              return {
                ...item,
                editingEquipment: {
                  ...item.editingEquipment,
                  uploadProgress: rest,
                },
              };
            })
          );
        }
      }, stepMs);

      fakeProgressTimers.current.set(timerKey, id);
    },
    []
  );

  const stopFakeProgress = useCallback((facilityId: string, key: string) => {
    const timerKey = `${facilityId}:${key}`;
    const id = fakeProgressTimers.current.get(timerKey);
    if (id !== undefined) {
      clearInterval(id);
      fakeProgressTimers.current.delete(timerKey);
    }
    setItems((prev) =>
      prev.map((item) => {
        if (item.entry.id !== facilityId || !item.editingEquipment) {
          return item;
        }
        const { [key]: _removed, ...rest } =
          item.editingEquipment.uploadProgress;
        return {
          ...item,
          editingEquipment: { ...item.editingEquipment, uploadProgress: rest },
        };
      })
    );
  }, []);

  // Load facility data when in edit mode
  useEffect(() => {
    if (mode !== 'edit' || !editFacilityId) {
      return;
    }

    const loadFacility = async () => {
      setIsLoading(true);
      try {
        const result = await getFacility(editFacilityId);
        if (result.success && result.data) {
          const facility = result.data.facility;
          const facilityEntry: FacilityEntry = {
            id: editFacilityId,
            name: facility.name || '',
            nip: formatNip(facility.nip || ''),
            phone: facility.phoneNumber || '',
            countryCode: facility.countryCode || '+48',
            address1: facility.addressLine1 || '',
            address2: facility.addressLine2 || '',
            city: facility.city || '',
            state: facility.state || '',
            zip: facility.zipCode || '',
            country: facility.country || 'Polska',
          };
          setItems([
            {
              entry: facilityEntry,
              errors: createEmptyFacilityErrors(),
              equipment: [createEmptyEquipmentSubItem()],
              tempFacilityId: editFacilityId, // Use editFacilityId as the server facility ID
              ...(facility.status
                ? {
                    facilityStatus: facility.status as
                      | 'active'
                      | 'inactive'
                      | 'cascaded',
                  }
                : {}),
            },
          ]);
        } else {
          setSnackbarError(
            result.error || EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR
          );
          setIsSnackbarOpen(true);
        }
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : EQUIPMENT_ERROR_MESSAGES.UNEXPECTED_ERROR;
        setSnackbarError(msg);
        setIsSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadFacility();
  }, [mode, editFacilityId]);

  const facilityList = items.map((item) => item.entry);
  const facilityErrorsList = items.map((item) => item.errors);

  const getTempFacilityId = useCallback(
    (facilityId: string): string | null =>
      items.find((item) => item.entry.id === facilityId)?.tempFacilityId ??
      null,
    [items]
  );

  const getIsEditMode = useCallback(
    (facilityId: string): boolean =>
      items.find((item) => item.entry.id === facilityId)?.isEditMode ?? false,
    [items]
  );

  // ── Per-facility data getters ──────────────────────────────────────────────

  const getEquipmentList = useCallback(
    (facilityId: string) =>
      items
        .find((i) => i.entry.id === facilityId)
        ?.equipment.map((e) => e.entry) ?? [],
    [items]
  );

  const getEquipmentErrors = useCallback(
    (facilityId: string) =>
      items
        .find((i) => i.entry.id === facilityId)
        ?.equipment.map((e) => e.errors) ?? [],
    [items]
  );

  const getEquipmentConflicts = useCallback(
    (facilityId: string) =>
      items.find((i) => i.entry.id === facilityId)?.equipmentConflicts ?? {},
    [items]
  );

  const clearEquipmentConflicts = useCallback((facilityId: string) => {
    setItems((prev) =>
      prev.map((i) => {
        if (i.entry.id !== facilityId) {
          return i;
        }

        const { equipmentConflicts: _, ...rest } = i;
        return rest;
      })
    );
  }, []);

  const getUploadProgress = useCallback(
    (facilityId: string) =>
      items
        .find((i) => i.entry.id === facilityId)
        ?.equipment.map((e) => e.uploadProgress) ?? [],
    [items]
  );

  const getFailedUploadFiles = useCallback(
    (facilityId: string) =>
      items
        .find((i) => i.entry.id === facilityId)
        ?.equipment.map((e) => e.failedUploadFiles) ?? [],
    [items]
  );

  const getEquipmentServerIds = useCallback(
    (facilityId: string) =>
      items
        .find((i) => i.entry.id === facilityId)
        ?.equipment.map((e) => e.serverEquipmentId) ?? [],
    [items]
  );

  // ── Editing equipment helpers ─────────────────────────────────────────────

  const getEditingEquipment = useCallback(
    (facilityId: string): EditingEquipmentState | undefined =>
      items.find((item) => item.entry.id === facilityId)?.editingEquipment,
    [items]
  );

  const startEquipmentEdit = useCallback(
    (facilityId: string, item: EquipmentListItem): void => {
      const inferType = (filename: string, hint?: string | null): string => {
        if (hint) {
          return hint;
        }
        const ext = filename.split('.').pop()?.toLowerCase();
        switch (ext) {
          case 'jpg':
          case 'jpeg':
            return 'image/jpeg';
          case 'png':
            return 'image/png';
          case 'pdf':
            return 'application/pdf';
          case 'docx':
            return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
          default:
            return 'application/octet-stream';
        }
      };

      const existingImageMeta: ExistingFileMeta | null = item.image?.filename
        ? {
            filename: item.image.filename,
            fileSize: item.image.fileSize ?? 0,
            fileType: inferType(item.image.filename, item.image.fileType),
            path: item.image.path ?? '',
            resourceId: item.image.resourceId ?? '',
            isUploaded: item.image.isUploaded ?? true,
          }
        : null;

      const existingDocsMeta: ExistingFileMeta[] = (item.docs ?? [])
        .filter((d): d is typeof d & { filename: string } => !!d.filename)
        .map((d) => ({
          filename: d.filename,
          fileSize: d.fileSize ?? 0,
          fileType: inferType(d.filename, d.fileType),
          path: d.path ?? '',
          resourceId: d.resourceId ?? '',
          isUploaded: d.isUploaded ?? true,
        }));

      setItems((prev) =>
        prev.map((fItem) =>
          fItem.entry.id === facilityId
            ? {
                ...fItem,
                editingEquipment: {
                  serverId: item.id,
                  equipmentId: item.equipmentId,
                  ...(item.categoryLabel && {
                    categoryLabel: item.categoryLabel,
                  }),
                  entry: {
                    ...createEmptyEquipmentEntry(),
                    id: item.id,
                    name: item.name,
                    description: item.description,
                    category: item.category ?? '',
                    serialNumber: item.serialNumber ?? '',
                    modelName: item.modelName ?? '',
                    dateOfProduction: item.dateOfProduction
                      ? new Date(item.dateOfProduction)
                      : undefined,
                  },
                  errors: createEmptyEquipmentErrors(),
                  uploadProgress: {},
                  failedUploadFiles: [],
                  existingImageMeta,
                  existingDocsMeta,
                  deletedDocPaths: [],
                },
              }
            : fItem
        )
      );
    },
    []
  );

  const cancelEquipmentEdit = useCallback((facilityId: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.entry.id === facilityId
          ? { ...item, editingEquipment: undefined }
          : item
      )
    );
  }, []);

  const updateEditEquipmentField = useCallback(
    (
      facilityId: string,
      field: keyof Pick<
        EquipmentEntry,
        'name' | 'description' | 'serialNumber' | 'modelName'
      >,
      value: string
    ) => {
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId && item.editingEquipment
            ? {
                ...item,
                editingEquipment: {
                  ...item.editingEquipment,
                  entry: { ...item.editingEquipment.entry, [field]: value },
                  errors: { ...item.editingEquipment.errors, [field]: '' },
                },
              }
            : item
        )
      );
    },
    []
  );

  const updateEditEquipmentCategory = useCallback(
    (facilityId: string, value: string) => {
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId && item.editingEquipment
            ? {
                ...item,
                editingEquipment: {
                  ...item.editingEquipment,
                  entry: { ...item.editingEquipment.entry, category: value },
                  errors: { ...item.editingEquipment.errors, category: '' },
                },
              }
            : item
        )
      );
    },
    []
  );

  const updateEditEquipmentDateOfProduction = useCallback(
    (facilityId: string, date: Date | undefined) => {
      const utcDate = date
        ? new Date(
            Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
          )
        : undefined;
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId && item.editingEquipment
            ? {
                ...item,
                editingEquipment: {
                  ...item.editingEquipment,
                  entry: {
                    ...item.editingEquipment.entry,
                    dateOfProduction: utcDate,
                  },
                  errors: {
                    ...item.editingEquipment.errors,
                    dateOfProduction: '',
                  },
                },
              }
            : item
        )
      );
    },
    []
  );

  const handleEditExistingImageDelete = useCallback((facilityId: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.entry.id === facilityId && item.editingEquipment
          ? {
              ...item,
              editingEquipment: {
                ...item.editingEquipment,
                existingImageMeta: null,
              },
            }
          : item
      )
    );
  }, []);

  const handleEditExistingDocDelete = useCallback(
    (facilityId: string, path: string) => {
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId && item.editingEquipment
            ? {
                ...item,
                editingEquipment: {
                  ...item.editingEquipment,
                  existingDocsMeta:
                    item.editingEquipment.existingDocsMeta.filter(
                      (d) => d.path !== path
                    ),
                  deletedDocPaths: [
                    ...item.editingEquipment.deletedDocPaths,
                    path,
                  ],
                },
              }
            : item
        )
      );
    },
    []
  );

  const handleEditEquipmentImageError = useCallback(
    (facilityId: string, message: string) => {
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId && item.editingEquipment
            ? {
                ...item,
                editingEquipment: {
                  ...item.editingEquipment,
                  errors: {
                    ...item.editingEquipment.errors,
                    imageFile: message,
                  },
                },
              }
            : item
        )
      );
    },
    []
  );

  const handleEditEquipmentDocsError = useCallback(
    (facilityId: string, message: string) => {
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId && item.editingEquipment
            ? {
                ...item,
                editingEquipment: {
                  ...item.editingEquipment,
                  errors: {
                    ...item.editingEquipment.errors,
                    docFiles: message,
                  },
                },
              }
            : item
        )
      );
    },
    []
  );

  const uploadEditFile = useCallback(
    async (
      facilityId: string,
      file: File,
      pathTemplate: string,
      slot: 'image' | 'docs',
      // When true (Save flow), upload happens silently — no progress bar shown.
      // Progress bar is already handled by the fake animation on file select.
      silent = false
    ): Promise<MediaResource> => {
      const key = `${slot}:${file.name}`;

      // Always clear the failed state for this key; only seed uploadProgress when not silent
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId && item.editingEquipment
            ? {
                ...item,
                editingEquipment: {
                  ...item.editingEquipment,
                  ...(silent
                    ? {}
                    : {
                        uploadProgress: {
                          ...item.editingEquipment.uploadProgress,
                          [key]: 0,
                        },
                      }),
                  failedUploadFiles:
                    item.editingEquipment.failedUploadFiles.filter(
                      (f) => f !== key
                    ),
                },
              }
            : item
        )
      );
      try {
        const presignedResponse = await getPresignedUrlsBatch({
          paths: [pathTemplate],
          generatePath: true,
        });
        const fileData = presignedResponse.files[0];
        if (!fileData) {
          throw new Error(EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR);
        }
        await uploadFileToS3(
          fileData.url,
          file,
          silent
            ? undefined
            : (bytesLoaded) => {
                setItems((prev) =>
                  prev.map((item) =>
                    item.entry.id === facilityId && item.editingEquipment
                      ? {
                          ...item,
                          editingEquipment: {
                            ...item.editingEquipment,
                            uploadProgress: {
                              ...item.editingEquipment.uploadProgress,
                              [key]: bytesLoaded,
                            },
                          },
                        }
                      : item
                  )
                );
              }
        );
        const resource: MediaResource = {
          path: fileData.path,
          resourceId: fileData.resourceId,
          isUploaded: true,
          filename: file.name,
          fileType: file.type,
          fileSize: file.size,
        };
        if (!silent) {
          setItems((prev) =>
            prev.map((item) => {
              if (item.entry.id !== facilityId || !item.editingEquipment) {
                return item;
              }
              const { [key]: _removed, ...rest } =
                item.editingEquipment.uploadProgress;
              return {
                ...item,
                editingEquipment: {
                  ...item.editingEquipment,
                  uploadProgress: rest,
                },
              };
            })
          );
        }
        return resource;
      } catch {
        setItems((prev) =>
          prev.map((item) => {
            if (item.entry.id !== facilityId || !item.editingEquipment) {
              return item;
            }
            const { [key]: _removed, ...rest } =
              item.editingEquipment.uploadProgress;
            return {
              ...item,
              editingEquipment: {
                ...item.editingEquipment,
                uploadProgress: rest,
                failedUploadFiles: [
                  ...item.editingEquipment.failedUploadFiles.filter(
                    (f) => f !== key
                  ),
                  key,
                ],
              },
            };
          })
        );
        throw new Error(EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR);
      }
    },
    []
  );

  const handleEditEquipmentImageChange = useCallback(
    (facilityId: string, files: File[]) => {
      const file = files[0];

      // Stop any in-progress fake animation for the previously selected image
      const facilityItem = items.find((i) => i.entry.id === facilityId);
      const prevImageFile = facilityItem?.editingEquipment?.entry.imageFile;
      if (prevImageFile) {
        stopFakeProgress(facilityId, `image:${prevImageFile.name}`);
      }

      setItems((prev) =>
        prev.map((item): FacilityItem => {
          if (item.entry.id !== facilityId || !item.editingEquipment) {
            return item;
          }
          const entry: EquipmentEntry = { ...item.editingEquipment.entry };
          if (file !== undefined) {
            entry.imageFile = file;
          } else {
            delete entry.imageFile;
          }
          delete entry.imageResource;
          return {
            ...item,
            editingEquipment: {
              ...item.editingEquipment,
              entry,
              errors: { ...item.editingEquipment.errors, imageFile: '' },
            },
          };
        })
      );

      if (file !== undefined) {
        startFakeProgress(facilityId, `image:${file.name}`, file.size);
      }
    },
    [items, startFakeProgress, stopFakeProgress]
  );

  const handleEditEquipmentDocsChange = useCallback(
    (facilityId: string, files: File[]) => {
      const capped = files.slice(0, 2);
      const facilityItem = items.find((i) => i.entry.id === facilityId);
      const editing = facilityItem?.editingEquipment;
      if (!editing) {
        return;
      }

      // Stop fake animations for docs that were removed
      const newNames = new Set(capped.map((f) => f.name));
      for (const prev of editing.entry.docFiles) {
        if (!newNames.has(prev.name)) {
          stopFakeProgress(facilityId, `docs:${prev.name}`);
        }
      }

      const existingResources = editing.entry.docResources;
      const keptResources: Record<string, MediaResource> = {};
      for (const f of capped) {
        const res = existingResources[f.name];
        if (res) {
          keptResources[f.name] = res;
        }
      }

      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId && item.editingEquipment
            ? {
                ...item,
                editingEquipment: {
                  ...item.editingEquipment,
                  entry: {
                    ...item.editingEquipment.entry,
                    docFiles: capped,
                    docResources: keptResources,
                  },
                  errors: { ...item.editingEquipment.errors, docFiles: '' },
                },
              }
            : item
        )
      );

      // Start fake progress for newly added docs
      const prevNames = new Set(editing.entry.docFiles.map((f) => f.name));
      for (const f of capped) {
        const isNew = !existingResources[f.name] && !prevNames.has(f.name);
        if (isNew) {
          startFakeProgress(facilityId, `docs:${f.name}`, f.size);
        }
      }
    },
    [items, startFakeProgress, stopFakeProgress]
  );

  const submitEquipmentEdit = useCallback(
    async (facilityId: string): Promise<void> => {
      const facilityItem = items.find((i) => i.entry.id === facilityId);
      const editing = facilityItem?.editingEquipment;
      if (!editing) {
        return;
      }

      const nameError = !editing.entry.name.trim()
        ? EQUIPMENT_ERROR_MESSAGES.REQUIRED
        : '';
      const descriptionError = !editing.entry.description.trim()
        ? EQUIPMENT_ERROR_MESSAGES.REQUIRED
        : '';
      const hasImage =
        editing.entry.imageFile ||
        editing.entry.imageResource ||
        editing.existingImageMeta;
      const imageError = !hasImage ? EQUIPMENT_ERROR_MESSAGES.REQUIRED : '';
      const editNewFieldErrors = validateEntryFields(editing.entry);
      const hasErrors = Boolean(
        nameError ||
        descriptionError ||
        imageError ||
        Object.values(editNewFieldErrors).some(Boolean)
      );
      const isUploading = Object.keys(editing.uploadProgress).length > 0;

      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId && item.editingEquipment
            ? {
                ...item,
                editingEquipment: {
                  ...item.editingEquipment,
                  errors: {
                    ...item.editingEquipment.errors,
                    name: nameError,
                    description: descriptionError,
                    imageFile: imageError,
                    ...editNewFieldErrors,
                  },
                },
              }
            : item
        )
      );

      if (hasErrors || isUploading) {
        return;
      }

      setIsSubmitting(true);
      try {
        // Upload new image if a file was selected but not yet uploaded
        let imageResource = editing.entry.imageResource;
        if (editing.entry.imageFile && !imageResource) {
          try {
            imageResource = await uploadEditFile(
              facilityId,
              editing.entry.imageFile,
              `equipments/entityid-placeholder/image/resourceid-placeholder/${editing.entry.imageFile.name}`,
              'image',
              true
            );
            setItems((prev) =>
              prev.map((item) =>
                item.entry.id === facilityId && item.editingEquipment
                  ? {
                      ...item,
                      editingEquipment: {
                        ...item.editingEquipment,
                        entry: {
                          ...item.editingEquipment.entry,
                          imageResource,
                        },
                      },
                    }
                  : item
              )
            );
          } catch {
            return; // uploadEditFile already set failedUploadFiles
          }
        }

        // Upload new doc files that haven't been uploaded yet
        const docResources: Record<string, MediaResource> = {
          ...editing.entry.docResources,
        };
        for (const file of editing.entry.docFiles) {
          if (!docResources[file.name]) {
            try {
              const resource = await uploadEditFile(
                facilityId,
                file,
                `equipments/entityid-placeholder/docs/resourceid-placeholder/${file.name}`,
                'docs',
                true
              );
              docResources[file.name] = resource;
              setItems((prev) =>
                prev.map((item) =>
                  item.entry.id === facilityId && item.editingEquipment
                    ? {
                        ...item,
                        editingEquipment: {
                          ...item.editingEquipment,
                          entry: {
                            ...item.editingEquipment.entry,
                            docResources: {
                              ...item.editingEquipment.entry.docResources,
                              [file.name]: resource,
                            },
                          },
                        },
                      }
                    : item
                )
              );
            } catch {
              return; // uploadEditFile already set failedUploadFiles
            }
          }
        }

        // Build PATCH payload
        const payload: Parameters<typeof editEquipment>[1] = {
          name: editing.entry.name.trim(),
          description: editing.entry.description.trim(),
          ...(editing.entry.category && { category: editing.entry.category }),
          serialNumber: editing.entry.serialNumber.trim(),
          modelName: editing.entry.modelName.trim(),
          ...(editing.entry.dateOfProduction && {
            dateOfProduction: editing.entry.dateOfProduction
              .toISOString()
              .split('T')[0],
          }),
        };

        // Image: send new upload OR pass existing back unchanged
        if (imageResource) {
          payload.image = imageResource;
        } else if (editing.existingImageMeta) {
          payload.image = {
            path: editing.existingImageMeta.path,
            resourceId: editing.existingImageMeta.resourceId,
            filename: editing.existingImageMeta.filename,
            fileType: editing.existingImageMeta.fileType,
            fileSize: editing.existingImageMeta.fileSize,
            isUploaded: editing.existingImageMeta.isUploaded,
          };
        }

        // Docs: combine remaining existing + newly uploaded
        const existingDocs: MediaResource[] = editing.existingDocsMeta.map(
          (m) => ({
            path: m.path,
            resourceId: m.resourceId,
            filename: m.filename,
            fileType: m.fileType,
            fileSize: m.fileSize,
            isUploaded: m.isUploaded,
          })
        );
        const newDocs: MediaResource[] = editing.entry.docFiles
          .map((f) => docResources[f.name])
          .filter((r): r is MediaResource => r !== undefined);
        const docs = [...existingDocs, ...newDocs];
        if (docs.length > 0 || editing.entry.docFiles.length > 0) {
          payload.docs = docs;
        }

        const result = await editEquipment(editing.serverId, payload);

        if (!result.success) {
          if (result.fieldErrors) {
            setItems((prev) =>
              prev.map((item) =>
                item.entry.id === facilityId && item.editingEquipment
                  ? {
                      ...item,
                      editingEquipment: {
                        ...item.editingEquipment,
                        errors: {
                          ...item.editingEquipment.errors,
                          serialNumber: result.fieldErrors?.serialNumber ?? '',
                        },
                      },
                    }
                  : item
              )
            );
            return;
          }
          setSnackbarError(
            result.error ?? EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR
          );
          setIsSnackbarOpen(true);
          return;
        }

        // Fire-and-forget: delete removed docs from S3 after PATCH succeeds.
        // Failures are non-blocking — the server record is already updated.
        if (editing.deletedDocPaths.length > 0) {
          void Promise.all(
            editing.deletedDocPaths.map((path) => deleteMediaResource(path))
          );
        }

        setItems((prev) =>
          prev.map((item) =>
            item.entry.id === facilityId
              ? { ...item, editingEquipment: undefined }
              : item
          )
        );
        setEquipmentEditCount((c) => c + 1);
        setIsEquipmentEditSuccessSnackbarOpen(true);
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : EQUIPMENT_ERROR_MESSAGES.UNEXPECTED_ERROR;
        setSnackbarError(msg);
        setIsSnackbarOpen(true);
      } finally {
        setIsSubmitting(false);
      }
    },
    [items, uploadEditFile]
  );

  const retryUploadSingleFile = useCallback(
    async (facilityId: string, key: string): Promise<void> => {
      // key format: "image:filename.jpg" or "docs:filename.pdf"
      const colonIdx = key.indexOf(':');
      if (colonIdx === -1) {
        return;
      }
      const slot = key.slice(0, colonIdx) as 'image' | 'docs';
      const filename = key.slice(colonIdx + 1);

      const facilityItem = items.find((i) => i.entry.id === facilityId);
      const editing = facilityItem?.editingEquipment;
      if (!editing) {
        return;
      }

      let file: File | undefined;
      if (slot === 'image') {
        file =
          editing.entry.imageFile?.name === filename
            ? editing.entry.imageFile
            : undefined;
      } else {
        file = editing.entry.docFiles.find((f) => f.name === filename);
      }

      if (!file) {
        // File no longer in the form — clear stale failed state
        setItems((prev) =>
          prev.map((item) =>
            item.entry.id === facilityId && item.editingEquipment
              ? {
                  ...item,
                  editingEquipment: {
                    ...item.editingEquipment,
                    failedUploadFiles:
                      item.editingEquipment.failedUploadFiles.filter(
                        (k) => k !== key
                      ),
                  },
                }
              : item
          )
        );
        return;
      }

      const pathTemplate =
        slot === 'image'
          ? `equipments/entityid-placeholder/image/resourceid-placeholder/${file.name}`
          : `equipments/entityid-placeholder/docs/resourceid-placeholder/${file.name}`;

      try {
        const resource = await uploadEditFile(
          facilityId,
          file,
          pathTemplate,
          slot,
          true
        );
        setItems((prev) =>
          prev.map((item) => {
            if (item.entry.id !== facilityId || !item.editingEquipment) {
              return item;
            }
            if (slot === 'image') {
              return {
                ...item,
                editingEquipment: {
                  ...item.editingEquipment,
                  entry: {
                    ...item.editingEquipment.entry,
                    imageResource: resource,
                  },
                },
              };
            }
            return {
              ...item,
              editingEquipment: {
                ...item.editingEquipment,
                entry: {
                  ...item.editingEquipment.entry,
                  docResources: {
                    ...item.editingEquipment.entry.docResources,
                    [filename]: resource,
                  },
                },
              },
            };
          })
        );
      } catch {
        // uploadEditFile already re-adds key to failedUploadFiles
      }
    },
    [items, uploadEditFile]
  );

  // ── Facility field operations ──────────────────────────────────────────────

  const addFacilityEntry = useCallback(() => {
    setItems((prev) => {
      // Check if the first item is an empty, unmodified facility (never submitted)
      const firstItem = prev[0];
      if (firstItem) {
        const isFirstEmpty =
          firstItem.tempFacilityId === null &&
          !firstItem.isEditMode &&
          !firstItem.entry.name.trim() &&
          !firstItem.entry.nip.trim() &&
          !firstItem.entry.phone.trim() &&
          !firstItem.entry.address1.trim() &&
          !firstItem.entry.city.trim() &&
          !firstItem.entry.state.trim() &&
          !firstItem.entry.zip.trim();

        // If the first item is empty and unmodified, don't add a duplicate
        if (isFirstEmpty) {
          return prev;
        }
      }

      // Otherwise, append a new facility
      return [...prev, createEmptyFacilityItem()];
    });
  }, []);

  const loadFacilityForEditing = useCallback(async (facilityId: string) => {
    // Immediately show empty edit form with shimmer
    const emptyEditItem: FacilityItem = {
      entry: { ...createEmptyFacilityEntry(), id: facilityId },
      errors: createEmptyFacilityErrors(),
      equipment: [],
      tempFacilityId: facilityId,
      isEditMode: true,
    };

    setItems((prev) => {
      const firstItem = prev[0];
      if (firstItem) {
        const isFirstEmpty =
          firstItem.tempFacilityId === null &&
          !firstItem.isEditMode &&
          !firstItem.entry.name.trim() &&
          !firstItem.entry.nip.trim() &&
          !firstItem.entry.phone.trim() &&
          !firstItem.entry.address1.trim() &&
          !firstItem.entry.city.trim() &&
          !firstItem.entry.state.trim() &&
          !firstItem.entry.zip.trim();

        if (isFirstEmpty) {
          return [emptyEditItem, ...prev.slice(1)];
        }
      }

      return [...prev, emptyEditItem];
    });

    // Set loading state for this specific facility
    setLoadingFacilityId(facilityId);

    try {
      const result = await getFacility(facilityId);
      if (result.success && result.data) {
        const facility = result.data.facility;
        const facilityEntry: FacilityEntry = {
          id: facilityId,
          name: facility.name || '',
          nip: formatNip(facility.nip || ''),
          phone: facility.phoneNumber || '',
          countryCode: facility.countryCode || '+48',
          address1: facility.addressLine1 || '',
          address2: facility.addressLine2 || '',
          city: facility.city || '',
          state: facility.state || '',
          zip: facility.zipCode || '',
          country: facility.country || 'Polska',
          facilityId: facility.facilityId || '',
        };

        // Update the empty form with fetched data
        setItems((prev) =>
          prev.map((item) =>
            item.entry.id === facilityId
              ? {
                  ...item,
                  entry: facilityEntry,
                  ...(facility.status
                    ? {
                        facilityStatus: facility.status as
                          | 'active'
                          | 'inactive'
                          | 'cascaded',
                      }
                    : {}),
                }
              : item
          )
        );
      } else {
        setSnackbarError(
          result.error || EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR
        );
        setIsSnackbarOpen(true);
      }
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : EQUIPMENT_ERROR_MESSAGES.UNEXPECTED_ERROR;
      setSnackbarError(msg);
      setIsSnackbarOpen(true);
    } finally {
      setLoadingFacilityId(null);
    }
  }, []);

  const updateFacilityField = useCallback(
    (
      facilityId: string,
      field: keyof Pick<
        FacilityEntry,
        | 'name'
        | 'nip'
        | 'phone'
        | 'address1'
        | 'address2'
        | 'city'
        | 'state'
        | 'zip'
      >,
      value: string
    ) => {
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId
            ? {
                ...item,
                entry: { ...item.entry, [field]: value },
                errors: { ...item.errors, [field]: '' },
              }
            : item
        )
      );
    },
    []
  );

  // NIP: keep only digits, format XXX-XXX-XX-XX, max 10 digits
  const handleFacilityNipChange = useCallback(
    (facilityId: string, raw: string) => {
      const digits = raw.replace(/\D/g, '').slice(0, 10);
      let formatted = digits;
      if (digits.length > 8) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
      } else if (digits.length > 6) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else if (digits.length > 3) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      }
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId
            ? {
                ...item,
                entry: { ...item.entry, nip: formatted },
                errors: { ...item.errors, nip: '' },
              }
            : item
        )
      );
    },
    []
  );

  // Phone: digits only, max 9
  const handleFacilityPhoneChange = useCallback(
    (facilityId: string, raw: string) => {
      const value = raw.replace(/\D/g, '').slice(0, 9);
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId
            ? {
                ...item,
                entry: { ...item.entry, phone: value },
                errors: { ...item.errors, phone: '' },
              }
            : item
        )
      );
    },
    []
  );

  // ── Equipment operations (scoped by facilityId) ────────────────────────────

  const addEquipmentEntry = useCallback((facilityId: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.entry.id === facilityId
          ? {
              ...item,
              equipment: [...item.equipment, createEmptyEquipmentSubItem()],
            }
          : item
      )
    );
  }, []);

  const removeEquipmentEntry = useCallback(
    (facilityId: string, equipmentId: string) => {
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId
            ? {
                ...item,
                equipment: item.equipment.filter(
                  (e) => e.entry.id !== equipmentId
                ),
              }
            : item
        )
      );
    },
    []
  );

  const resetFacilityFields = useCallback((facilityId: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.entry.id === facilityId
          ? {
              ...item,
              entry: { ...createEmptyFacilityEntry(), id: facilityId },
              errors: createEmptyFacilityErrors(),
            }
          : item
      )
    );
  }, []);

  const exitEditMode = useCallback((facilityId: string) => {
    setItems((prev) => prev.filter((item) => item.entry.id !== facilityId));
  }, []);

  const resetEquipmentEntry = useCallback(
    (facilityId: string, equipmentId: string) => {
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId
            ? {
                ...item,
                equipment: item.equipment.map((e) =>
                  e.entry.id === equipmentId
                    ? {
                        ...createEmptyEquipmentSubItem(),
                        entry: {
                          ...createEmptyEquipmentEntry(),
                          id: equipmentId,
                        },
                      }
                    : e
                ),
              }
            : item
        )
      );
    },
    []
  );

  const updateEquipmentField = useCallback(
    (
      facilityId: string,
      equipmentId: string,
      field: keyof Pick<
        EquipmentEntry,
        'name' | 'description' | 'serialNumber' | 'modelName'
      >,
      value: string
    ) => {
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId
            ? {
                ...item,
                equipment: item.equipment.map((e) =>
                  e.entry.id === equipmentId
                    ? {
                        ...e,
                        entry: { ...e.entry, [field]: value },
                        errors: { ...e.errors, [field]: '' },
                      }
                    : e
                ),
              }
            : item
        )
      );
    },
    []
  );

  const updateEquipmentCategory = useCallback(
    (facilityId: string, equipmentId: string, value: string) => {
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId
            ? {
                ...item,
                equipment: item.equipment.map((e) =>
                  e.entry.id === equipmentId
                    ? {
                        ...e,
                        entry: { ...e.entry, category: value },
                        errors: { ...e.errors, category: '' },
                      }
                    : e
                ),
              }
            : item
        )
      );
    },
    []
  );

  const updateEquipmentDateOfProduction = useCallback(
    (facilityId: string, equipmentId: string, date: Date | undefined) => {
      const utcDate = date
        ? new Date(
            Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
          )
        : undefined;
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId
            ? {
                ...item,
                equipment: item.equipment.map((e) =>
                  e.entry.id === equipmentId
                    ? {
                        ...e,
                        entry: { ...e.entry, dateOfProduction: utcDate },
                        errors: { ...e.errors, dateOfProduction: '' },
                      }
                    : e
                ),
              }
            : item
        )
      );
    },
    []
  );

  // ── File upload helper ───────────────────────────────────────────────────────

  const uploadFile = useCallback(
    async (
      facilityId: string,
      equipmentId: string,
      file: File,
      pathTemplate: string,
      slot: 'image' | 'docs'
    ): Promise<MediaResource> => {
      const key = `${slot}:${file.name}`;
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId
            ? {
                ...item,
                equipment: item.equipment.map((e) =>
                  e.entry.id === equipmentId
                    ? {
                        ...e,
                        uploadProgress: { ...e.uploadProgress, [key]: 0 },
                        failedUploadFiles: e.failedUploadFiles.filter(
                          (f) => f !== key
                        ),
                      }
                    : e
                ),
              }
            : item
        )
      );

      try {
        const presignedResponse = await getPresignedUrlsBatch({
          paths: [pathTemplate],
          generatePath: true,
        });

        const fileData = presignedResponse.files[0];
        if (!fileData) {
          throw new Error(EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR);
        }

        await uploadFileToS3(fileData.url, file, (bytesLoaded) => {
          setItems((prev) =>
            prev.map((item) =>
              item.entry.id === facilityId
                ? {
                    ...item,
                    equipment: item.equipment.map((e) =>
                      e.entry.id === equipmentId
                        ? {
                            ...e,
                            uploadProgress: {
                              ...e.uploadProgress,
                              [key]: bytesLoaded,
                            },
                          }
                        : e
                    ),
                  }
                : item
            )
          );
        });

        const resource: MediaResource = {
          path: fileData.path,
          resourceId: fileData.resourceId,
          isUploaded: true,
          filename: file.name,
          fileType: file.type,
          fileSize: file.size,
        };

        setItems((prev) =>
          prev.map((item) =>
            item.entry.id === facilityId
              ? {
                  ...item,
                  equipment: item.equipment.map((e) => {
                    if (e.entry.id !== equipmentId) {
                      return e;
                    }
                    const { [key]: _removed, ...rest } = e.uploadProgress;
                    return { ...e, uploadProgress: rest };
                  }),
                }
              : item
          )
        );

        return resource;
      } catch {
        setItems((prev) =>
          prev.map((item) =>
            item.entry.id === facilityId
              ? {
                  ...item,
                  equipment: item.equipment.map((e) => {
                    if (e.entry.id !== equipmentId) {
                      return e;
                    }
                    const { [key]: _removed, ...rest } = e.uploadProgress;
                    return {
                      ...e,
                      uploadProgress: rest,
                      failedUploadFiles: [
                        ...e.failedUploadFiles.filter((f) => f !== key),
                        key,
                      ],
                    };
                  }),
                }
              : item
          )
        );
        throw new Error(EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR);
      }
    },
    []
  );

  // ── Image handlers ───────────────────────────────────────────────────────────

  const handleEquipmentImageChange = useCallback(
    (facilityId: string, equipmentId: string, files: File[]) => {
      const file = files[0];

      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId
            ? {
                ...item,
                equipment: item.equipment.map((e): EquipmentSubItem => {
                  if (e.entry.id !== equipmentId) {
                    return e;
                  }
                  const entry: EquipmentEntry = { ...e.entry };
                  if (file !== undefined) {
                    entry.imageFile = file;
                  } else {
                    delete entry.imageFile;
                  }
                  delete entry.imageResource;
                  return {
                    ...e,
                    entry,
                    errors: { ...e.errors, imageFile: '' },
                  };
                }),
              }
            : item
        )
      );

      if (!file) {
        return;
      }

      void uploadFile(
        facilityId,
        equipmentId,
        file,
        `equipments/entityid-placeholder/image/resourceid-placeholder/${file.name}`,
        'image'
      )
        .then((resource) => {
          setItems((prev) =>
            prev.map((item) =>
              item.entry.id === facilityId
                ? {
                    ...item,
                    equipment: item.equipment.map((e) =>
                      e.entry.id === equipmentId
                        ? {
                            ...e,
                            entry: { ...e.entry, imageResource: resource },
                          }
                        : e
                    ),
                  }
                : item
            )
          );
        })
        .catch(() => {});
    },
    [uploadFile]
  );

  const handleEquipmentImageError = useCallback(
    (facilityId: string, equipmentId: string, message: string) => {
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId
            ? {
                ...item,
                equipment: item.equipment.map((e) =>
                  e.entry.id === equipmentId
                    ? { ...e, errors: { ...e.errors, imageFile: message } }
                    : e
                ),
              }
            : item
        )
      );
    },
    []
  );

  // ── Docs handlers ────────────────────────────────────────────────────────────

  const handleEquipmentDocsChange = useCallback(
    (facilityId: string, equipmentId: string, files: File[]) => {
      const capped = files.slice(0, 2);
      const facilityItem = items.find((i) => i.entry.id === facilityId);
      const equipItem = facilityItem?.equipment.find(
        (e) => e.entry.id === equipmentId
      );
      if (!equipItem) {
        return;
      }

      const existingResources = equipItem.entry.docResources;
      const newFiles = capped.filter((f) => !existingResources[f.name]);
      const keptResources: Record<string, MediaResource> = {};
      for (const f of capped) {
        const res = existingResources[f.name];
        if (res) {
          keptResources[f.name] = res;
        }
      }

      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId
            ? {
                ...item,
                equipment: item.equipment.map((e) =>
                  e.entry.id === equipmentId
                    ? {
                        ...e,
                        entry: {
                          ...e.entry,
                          docFiles: capped,
                          docResources: keptResources,
                        },
                        errors: { ...e.errors, docFiles: '' },
                      }
                    : e
                ),
              }
            : item
        )
      );

      for (const file of newFiles) {
        void uploadFile(
          facilityId,
          equipmentId,
          file,
          `equipments/entityid-placeholder/docs/resourceid-placeholder/${file.name}`,
          'docs'
        )
          .then((resource) => {
            setItems((prev) =>
              prev.map((item) =>
                item.entry.id === facilityId
                  ? {
                      ...item,
                      equipment: item.equipment.map((e) =>
                        e.entry.id === equipmentId
                          ? {
                              ...e,
                              entry: {
                                ...e.entry,
                                docResources: {
                                  ...e.entry.docResources,
                                  [file.name]: resource,
                                },
                              },
                            }
                          : e
                      ),
                    }
                  : item
              )
            );
          })
          .catch(() => {});
      }
    },
    [items, uploadFile]
  );

  const handleEquipmentDocsError = useCallback(
    (facilityId: string, equipmentId: string, message: string) => {
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId
            ? {
                ...item,
                equipment: item.equipment.map((e) =>
                  e.entry.id === equipmentId
                    ? { ...e, errors: { ...e.errors, docFiles: message } }
                    : e
                ),
              }
            : item
        )
      );
    },
    []
  );

  // ── Retry upload ─────────────────────────────────────────────────────────────

  const retryEquipmentUpload = useCallback(
    async (
      facilityId: string,
      equipmentId: string,
      filename: string
    ): Promise<void> => {
      const facilityItem = items.find((i) => i.entry.id === facilityId);
      const equipItem = facilityItem?.equipment.find(
        (e) => e.entry.id === equipmentId
      );
      if (!equipItem) {
        return;
      }

      const isImage = filename.startsWith('image:');
      const actualFilename = isImage ? filename.slice(6) : filename.slice(5);
      const file = isImage
        ? equipItem.entry.imageFile
        : equipItem.entry.docFiles.find((f) => f.name === actualFilename);
      if (!file) {
        return;
      }

      const slot: 'image' | 'docs' = isImage ? 'image' : 'docs';
      const pathTemplate = isImage
        ? `equipments/entityid-placeholder/image/resourceid-placeholder/${actualFilename}`
        : `equipments/entityid-placeholder/docs/resourceid-placeholder/${actualFilename}`;

      try {
        const resource = await uploadFile(
          facilityId,
          equipmentId,
          file,
          pathTemplate,
          slot
        );
        setItems((prev) =>
          prev.map((item) =>
            item.entry.id === facilityId
              ? {
                  ...item,
                  equipment: item.equipment.map((e) => {
                    if (e.entry.id !== equipmentId) {
                      return e;
                    }
                    if (isImage) {
                      return {
                        ...e,
                        entry: { ...e.entry, imageResource: resource },
                      };
                    }
                    return {
                      ...e,
                      entry: {
                        ...e.entry,
                        docResources: {
                          ...e.entry.docResources,
                          [actualFilename]: resource,
                        },
                      },
                    };
                  }),
                }
              : item
          )
        );
      } catch {
        // uploadFile already sets failedUploadFiles in state
      }
    },
    [items, uploadFile]
  );

  // ── Validation ───────────────────────────────────────────────────────────────

  const validateAll = useCallback((): boolean => {
    let hasErrors = false;
    let hasUploadInProgress = false;
    let hasUploadFailed = false;

    const validatedItems = items.map((item) => {
      const nameError = !item.entry.name.trim()
        ? EQUIPMENT_ERROR_MESSAGES.REQUIRED
        : '';
      const nipError = validateNip(item.entry.nip);
      const phoneError = validatePhone(item.entry.phone);
      const address1Error = !item.entry.address1.trim()
        ? EQUIPMENT_ERROR_MESSAGES.REQUIRED
        : '';
      const cityError = !item.entry.city.trim()
        ? EQUIPMENT_ERROR_MESSAGES.REQUIRED
        : '';
      const stateError = !item.entry.state.trim()
        ? EQUIPMENT_ERROR_MESSAGES.REQUIRED
        : '';
      const zipError = validateZip(item.entry.zip);

      if (
        nameError ||
        nipError ||
        phoneError ||
        address1Error ||
        cityError ||
        stateError ||
        zipError
      ) {
        hasErrors = true;
      }

      const validatedEquipment = item.equipment.map((e) => {
        // Skip validation for equipment already submitted to API
        if (e.serverEquipmentId) {
          return e;
        }

        if (Object.keys(e.uploadProgress).length > 0) {
          hasUploadInProgress = true;
        }
        if (e.failedUploadFiles.length > 0) {
          hasUploadFailed = true;
        }

        // Skip completely empty equipment (user left the slot blank)
        const isEmpty =
          !e.entry.name.trim() &&
          !e.entry.description.trim() &&
          !e.entry.imageFile;
        if (isEmpty) {
          return e;
        }

        const equipNameError = !e.entry.name.trim()
          ? EQUIPMENT_ERROR_MESSAGES.REQUIRED
          : '';
        const equipDescError = !e.entry.description.trim()
          ? EQUIPMENT_ERROR_MESSAGES.REQUIRED
          : e.entry.description.length > 500
            ? EQUIPMENT_ERROR_MESSAGES.DESCRIPTION_TOO_LONG
            : '';
        const equipImageError = !e.entry.imageFile
          ? EQUIPMENT_ERROR_MESSAGES.REQUIRED
          : !e.entry.imageResource
            ? EQUIPMENT_ERROR_MESSAGES.UPLOAD_INCOMPLETE
            : '';
        const newFieldErrors = validateEntryFields(e.entry);

        if (
          equipNameError ||
          equipDescError ||
          equipImageError ||
          newFieldErrors.category ||
          newFieldErrors.serialNumber ||
          newFieldErrors.modelName ||
          newFieldErrors.dateOfProduction
        ) {
          hasErrors = true;
        }

        return {
          ...e,
          errors: {
            ...e.errors,
            name: equipNameError,
            description: equipDescError,
            imageFile: equipImageError,
            ...newFieldErrors,
          },
        };
      });

      return {
        ...item,
        errors: {
          name: nameError,
          nip: nipError,
          phone: phoneError,
          address1: address1Error,
          city: cityError,
          state: stateError,
          zip: zipError,
        },
        equipment: validatedEquipment,
      };
    });

    setItems(validatedItems);
    return !hasErrors && !hasUploadInProgress && !hasUploadFailed;
  }, [items]);

  // ── Validate a single equipment entry (surfaces errors, no API call) ─────────

  const validateEquipmentEntry = useCallback(
    (facilityId: string, equipmentId: string): void => {
      setItems((prev) =>
        prev.map((item) => {
          if (item.entry.id !== facilityId) {
            return item;
          }
          return {
            ...item,
            equipment: item.equipment.map((e) => {
              if (e.entry.id !== equipmentId) {
                return e;
              }
              const nameError = !e.entry.name.trim()
                ? EQUIPMENT_ERROR_MESSAGES.REQUIRED
                : '';
              const descriptionError = !e.entry.description.trim()
                ? EQUIPMENT_ERROR_MESSAGES.REQUIRED
                : e.entry.description.length > 500
                  ? EQUIPMENT_ERROR_MESSAGES.DESCRIPTION_TOO_LONG
                  : '';
              const imageFileError = !e.entry.imageFile
                ? EQUIPMENT_ERROR_MESSAGES.REQUIRED
                : !e.entry.imageResource
                  ? EQUIPMENT_ERROR_MESSAGES.UPLOAD_INCOMPLETE
                  : '';
              const newFieldErrors = validateEntryFields(e.entry);
              return {
                ...e,
                errors: {
                  ...e.errors,
                  name: nameError,
                  description: descriptionError,
                  imageFile: imageFileError,
                  ...newFieldErrors,
                },
              };
            }),
          };
        })
      );
    },
    []
  );

  // ── Submit equipment entry: validate → create temp facility if needed → POST equipment ──

  const submitEquipmentEntry = useCallback(
    async (
      clientId: string,
      facilityId: string,
      equipmentId: string
    ): Promise<void> => {
      const facilityItem = items.find((i) => i.entry.id === facilityId);
      if (!facilityItem) {
        return;
      }

      const equipItem = facilityItem.equipment.find(
        (e) => e.entry.id === equipmentId
      );
      if (!equipItem) {
        return;
      }

      // Validate
      const nameError = !equipItem.entry.name.trim()
        ? EQUIPMENT_ERROR_MESSAGES.REQUIRED
        : '';
      const descriptionError = !equipItem.entry.description.trim()
        ? EQUIPMENT_ERROR_MESSAGES.REQUIRED
        : equipItem.entry.description.length > 500
          ? EQUIPMENT_ERROR_MESSAGES.DESCRIPTION_TOO_LONG
          : '';
      const imageFileError = !equipItem.entry.imageFile
        ? EQUIPMENT_ERROR_MESSAGES.REQUIRED
        : !equipItem.entry.imageResource
          ? EQUIPMENT_ERROR_MESSAGES.UPLOAD_INCOMPLETE
          : '';

      const newFieldErrors = validateEntryFields(equipItem.entry);
      const hasErrors = Boolean(
        nameError ||
        descriptionError ||
        imageFileError ||
        Object.values(newFieldErrors).some(Boolean)
      );
      const hasUploadInProgress =
        Object.keys(equipItem.uploadProgress).length > 0;
      const hasUploadFailed = equipItem.failedUploadFiles.length > 0;

      // Always surface validation errors
      setItems((prev) =>
        prev.map((item) => {
          if (item.entry.id !== facilityId) {
            return item;
          }
          return {
            ...item,
            equipment: item.equipment.map((e) => {
              if (e.entry.id !== equipmentId) {
                return e;
              }
              return {
                ...e,
                errors: {
                  ...e.errors,
                  name: nameError,
                  description: descriptionError,
                  imageFile: imageFileError,
                  ...newFieldErrors,
                },
              };
            }),
          };
        })
      );

      if (hasErrors || hasUploadInProgress || hasUploadFailed) {
        return;
      }

      const trimmedSerial = equipItem.entry.serialNumber?.trim();
      if (
        trimmedSerial &&
        items.some((fItem) =>
          fItem.equipment.some(
            (e) =>
              !(fItem.entry.id === facilityId && e.entry.id === equipmentId) &&
              e.entry.serialNumber?.trim() === trimmedSerial
          )
        )
      ) {
        setItems((prev) =>
          prev.map((item) => {
            if (item.entry.id !== facilityId) {
              return item;
            }
            return {
              ...item,
              equipment: item.equipment.map((e) => {
                if (e.entry.id !== equipmentId) {
                  return e;
                }
                return {
                  ...e,
                  errors: {
                    ...e.errors,
                    serialNumber:
                      EQUIPMENT_ERROR_MESSAGES.SERIAL_NUMBER_DUPLICATE,
                  },
                };
              }),
            };
          })
        );
        return;
      }

      setIsSubmitting(true);
      try {
        // 1. Create temp facility on first equipment submission for this slot
        let tempId = facilityItem.tempFacilityId;

        if (!tempId) {
          const tempResult = await createTempFacility(clientId);
          if (!tempResult.success || !tempResult.facilityId) {
            setSnackbarError(
              tempResult.error ?? EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR
            );
            setIsSnackbarOpen(true);
            return;
          }
          tempId = tempResult.facilityId;

          // Persist tempFacilityId in sessionStorage so it survives page refresh
          const currentIds = getFacilitySession(clientId);
          setFacilitySession(clientId, [...currentIds, tempId]);

          // Save tempFacilityId into state immediately so it is reused on retry
          // regardless of whether the equipment POST below succeeds or fails
          setItems((prev) =>
            prev.map((item) =>
              item.entry.id === facilityId
                ? { ...item, tempFacilityId: tempId }
                : item
            )
          );
        }

        // 2. POST equipment
        const docs: MediaResource[] | undefined =
          equipItem.entry.docFiles.length > 0
            ? equipItem.entry.docFiles
                .map((f) => equipItem.entry.docResources[f.name])
                .filter((r): r is MediaResource => r !== undefined)
            : undefined;

        const equipDateOfProduction = equipItem.entry.dateOfProduction
          ? equipItem.entry.dateOfProduction.toISOString().split('T')[0]
          : undefined;

        const equipResult = await addEquipment(
          tempId,
          clientId,
          undefined,
          equipItem.entry.name,
          equipItem.entry.description,
          equipItem.entry.category,
          equipItem.entry.serialNumber || undefined,
          equipDateOfProduction,
          equipItem.entry.modelName || undefined,
          equipItem.entry.imageResource,
          docs
        );

        if (!equipResult.success) {
          if (equipResult.fieldErrors) {
            setItems((prev) =>
              prev.map((item) => {
                if (item.entry.id !== facilityId) {
                  return item;
                }
                return {
                  ...item,
                  equipment: item.equipment.map((e) => {
                    if (e.entry.id !== equipmentId) {
                      return e;
                    }
                    return {
                      ...e,
                      errors: {
                        ...e.errors,
                        serialNumber:
                          equipResult.fieldErrors?.serialNumber ?? '',
                      },
                    };
                  }),
                };
              })
            );
            return;
          }
          setSnackbarError(
            equipResult.error ?? EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR
          );
          setIsSnackbarOpen(true);
          return;
        }

        // 3. In edit mode only: re-finalize to activate the newly added draft equipment.
        // In add mode the facility is still a draft — finalization happens later
        // when the user clicks "Add Facility" (submitFacilityOnly).
        if (facilityItem.isEditMode) {
          const finalizeResult = await finalizeFacility(clientId, tempId);
          if (!finalizeResult.success) {
            if (finalizeResult.serialNumberConflict) {
              const conflictingSerial = finalizeResult.serialNumberConflict;
              setItems((prev) =>
                prev.map((item) => {
                  if (item.entry.id !== facilityId) {
                    return item;
                  }
                  return {
                    ...item,
                    equipment: item.equipment.map((e) =>
                      e.entry.serialNumber?.trim() === conflictingSerial
                        ? {
                            ...e,
                            errors: {
                              ...e.errors,
                              serialNumber:
                                EQUIPMENT_ERROR_MESSAGES.SERIAL_NUMBER_DUPLICATE,
                            },
                          }
                        : e
                    ),
                  };
                })
              );
              return;
            }
            setSnackbarError(
              finalizeResult.error ?? EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR
            );
            setIsSnackbarOpen(true);
            return;
          }
        }

        const serverEquipmentId = (
          equipResult.data as { equipment: { id: string; equipmentId: string } }
        ).equipment.equipmentId;

        // 3. Mark equipment as submitted
        setItems((prev) =>
          prev.map((item) => {
            if (item.entry.id !== facilityId) {
              return item;
            }
            return {
              ...item,
              equipment: item.equipment.map((e) =>
                e.entry.id === equipmentId ? { ...e, serverEquipmentId } : e
              ),
            };
          })
        );
        setIsEquipmentSuccessSnackbarOpen(true);
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : EQUIPMENT_ERROR_MESSAGES.UNEXPECTED_ERROR;
        setSnackbarError(msg);
        setIsSnackbarOpen(true);
      } finally {
        setIsSubmitting(false);
      }
    },
    [items]
  );

  // ── Submit facility only: validate → PATCH (if temp) or POST → success toast ──

  const submitFacilityOnly = useCallback(
    async (clientId: string, facilityItemId: string): Promise<void> => {
      const item = items.find((i) => i.entry.id === facilityItemId);
      if (!item) {
        return;
      }

      const nameError = !item.entry.name.trim()
        ? EQUIPMENT_ERROR_MESSAGES.REQUIRED
        : '';
      const nipError = validateNip(item.entry.nip);
      const phoneError = validatePhone(item.entry.phone);
      const address1Error = !item.entry.address1.trim()
        ? EQUIPMENT_ERROR_MESSAGES.REQUIRED
        : '';
      const cityError = !item.entry.city.trim()
        ? EQUIPMENT_ERROR_MESSAGES.REQUIRED
        : '';
      const stateError = !item.entry.state.trim()
        ? EQUIPMENT_ERROR_MESSAGES.REQUIRED
        : '';
      const zipError = validateZip(item.entry.zip);

      const hasErrors = Boolean(
        nameError ||
        nipError ||
        phoneError ||
        address1Error ||
        cityError ||
        stateError ||
        zipError
      );

      setItems((prev) =>
        prev.map((i) =>
          i.entry.id === facilityItemId
            ? {
                ...i,
                errors: {
                  name: nameError,
                  nip: nipError,
                  phone: phoneError,
                  address1: address1Error,
                  city: cityError,
                  state: stateError,
                  zip: zipError,
                },
              }
            : i
        )
      );

      if (hasErrors) {
        return;
      }

      setIsSubmitting(true);
      setIsFacilitySubmitting(true);
      try {
        const { entry, tempFacilityId } = item;
        const fields = {
          name: entry.name,
          nip: entry.nip.replace(/\D/g, ''),
          phoneNumber: entry.phone,
          countryCode: entry.countryCode,
          addressLine1: entry.address1,
          city: entry.city,
          state: entry.state,
          country: entry.country,
          addressLine2: entry.address2,
          zipCode: entry.zip,
        };

        let result;
        if (tempFacilityId) {
          // Facility already exists (equipment was added) — PATCH with mandatory check
          result = await patchFacility(tempFacilityId, fields, true);
        } else {
          // No equipment was added — use original POST flow
          result = await addFacility(
            clientId,
            fields.name,
            fields.nip,
            fields.phoneNumber,
            fields.countryCode,
            fields.addressLine1,
            fields.city,
            fields.state,
            fields.country,
            fields.addressLine2,
            fields.zipCode
          );
        }

        if (!result.success) {
          setSnackbarError(
            result.isSubscriptionLimitReached
              ? FACILITY_CREATE_ERROR_MESSAGES.LIMIT_REACHED_SNACKBAR()
              : (result.error ?? EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR)
          );
          setIsSnackbarOpen(true);
          return;
        }

        // Only finalize when adding a NEW facility. Editing an existing facility
        // (isEditMode=true) just patches fields — it is already finalized.
        if (!item.isEditMode) {
          const idToFinalize =
            tempFacilityId ?? (result as { facilityId?: string }).facilityId;
          if (!idToFinalize) {
            setSnackbarError(EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR);
            setIsSnackbarOpen(true);
            return;
          }
          const finalizeResult = await finalizeFacility(clientId, idToFinalize);
          if (!finalizeResult.success) {
            if (finalizeResult.serialNumberConflict) {
              const conflictingSerial = finalizeResult.serialNumberConflict;
              setItems((prev) =>
                prev.map((fItem) => {
                  if (fItem.entry.id !== item.entry.id) {
                    return fItem;
                  }
                  const conflicting = fItem.equipment.find(
                    (e) =>
                      e.entry.serialNumber?.trim() === conflictingSerial &&
                      e.serverEquipmentId
                  );
                  if (!conflicting) {
                    return fItem;
                  }
                  const conflictId = conflicting.serverEquipmentId ?? '';
                  return {
                    ...fItem,
                    equipmentConflicts: {
                      ...fItem.equipmentConflicts,
                      [conflictId]:
                        EQUIPMENT_ERROR_MESSAGES.SERIAL_NUMBER_DUPLICATE,
                    },
                  };
                })
              );
              return;
            }
            setSnackbarError(
              finalizeResult.error ?? EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR
            );
            setIsSnackbarOpen(true);
            return;
          }
        }

        // Remove from sessionStorage once the facility is finalized
        if (tempFacilityId) {
          const currentIds = getFacilitySession(clientId);
          setFacilitySession(
            clientId,
            currentIds.filter((id) => id !== tempFacilityId)
          );
        }

        // Edit mode: remove the item so the empty form never flashes while
        // the parent component restores the list via its refresh-key mechanism.
        // Add mode: reset to empty so the user can immediately add another facility.
        setItems((prev) =>
          item.isEditMode
            ? prev.filter((i) => i.entry.id !== facilityItemId)
            : prev.map((i) =>
                i.entry.id === facilityItemId ? createEmptyFacilityItem() : i
              )
        );
        setSavedFacilityCount((prev) => prev + 1);
        setSuccessMessage(
          item.isEditMode
            ? FACILITY_LABELS.EDIT_FACILITY_SUCCESS
            : FACILITY_LABELS.ADD_FACILITY_SUCCESS
        );
        setIsSuccessSnackbarOpen(true);
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : EQUIPMENT_ERROR_MESSAGES.UNEXPECTED_ERROR;
        setSnackbarError(msg);
        setIsSnackbarOpen(true);
      } finally {
        setIsSubmitting(false);
        setIsFacilitySubmitting(false);
      }
    },
    [items]
  );

  // ── Submit: finalize all facilities + equipment → finalize client ─────────────

  const submit = useCallback(
    async (clientId: string): Promise<void> => {
      if (!validateAll()) {
        return;
      }

      setIsSubmitting(true);
      try {
        for (const item of items) {
          const { entry, tempFacilityId } = item;

          const fields = {
            name: entry.name,
            nip: entry.nip.replace(/\D/g, ''),
            phoneNumber: entry.phone,
            countryCode: entry.countryCode,
            addressLine1: entry.address1,
            city: entry.city,
            state: entry.state,
            country: entry.country,
            addressLine2: entry.address2,
            zipCode: entry.zip,
          };

          let facilityDbId: string;

          if (tempFacilityId) {
            // PATCH — facility exists, just finalize the profile fields
            const patchResult = await patchFacility(
              tempFacilityId,
              fields,
              true
            );
            if (!patchResult.success || !patchResult.facilityId) {
              throw new Error(
                patchResult.error || EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR
              );
            }
            facilityDbId = tempFacilityId;

            // Submit any equipment that wasn't yet POSTed (no serverEquipmentId)
            for (const equipItem of item.equipment) {
              if (equipItem.serverEquipmentId) {
                continue;
              }

              const { entry: equipEntry } = equipItem;
              if (
                !equipEntry.name.trim() &&
                !equipEntry.description.trim() &&
                !equipEntry.imageFile
              ) {
                continue;
              }

              const docs: MediaResource[] | undefined =
                equipEntry.docFiles.length > 0
                  ? equipEntry.docFiles
                      .map((f) => equipEntry.docResources[f.name])
                      .filter((r): r is MediaResource => r !== undefined)
                  : undefined;

              const equipDateOfProduction = equipEntry.dateOfProduction
                ? equipEntry.dateOfProduction.toISOString().split('T')[0]
                : undefined;

              const result = await addEquipment(
                facilityDbId,
                clientId,
                undefined,
                equipEntry.name,
                equipEntry.description,
                equipEntry.category,
                equipEntry.serialNumber || undefined,
                equipDateOfProduction,
                equipEntry.modelName || undefined,
                equipEntry.imageResource,
                docs
              );

              if (!result.success) {
                throw new Error(
                  result.error || EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR
                );
              }
            }
          } else {
            // POST — no temp facility, create fresh
            const facilityResult = await addFacility(
              clientId,
              fields.name,
              fields.nip,
              fields.phoneNumber,
              fields.countryCode,
              fields.addressLine1,
              fields.city,
              fields.state,
              fields.country,
              fields.addressLine2,
              fields.zipCode
            );

            if (!facilityResult.success || !facilityResult.facilityId) {
              throw new Error(
                facilityResult.error || EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR
              );
            }
            facilityDbId = facilityResult.facilityId;

            // Create equipment for this facility
            for (const equipItem of item.equipment) {
              const { entry: equipEntry } = equipItem;

              if (
                !equipEntry.name.trim() &&
                !equipEntry.description.trim() &&
                !equipEntry.imageFile
              ) {
                continue;
              }

              const docs: MediaResource[] | undefined =
                equipEntry.docFiles.length > 0
                  ? equipEntry.docFiles
                      .map((f) => equipEntry.docResources[f.name])
                      .filter((r): r is MediaResource => r !== undefined)
                  : undefined;

              const equipDateOfProduction = equipEntry.dateOfProduction
                ? equipEntry.dateOfProduction.toISOString().split('T')[0]
                : undefined;

              const result = await addEquipment(
                facilityDbId,
                clientId,
                undefined,
                equipEntry.name,
                equipEntry.description,
                equipEntry.category,
                equipEntry.serialNumber || undefined,
                equipDateOfProduction,
                equipEntry.modelName || undefined,
                equipEntry.imageResource,
                docs
              );

              if (!result.success) {
                throw new Error(
                  result.error || EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR
                );
              }
            }
          }
        }

        // Finalize client
        const finalizeResult = await finalizeClient(clientId);
        if (!finalizeResult.success) {
          throw new Error(
            finalizeResult.error || EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR
          );
        }

        // Clear sessionStorage — all facilities finalized
        clearFacilitySession(clientId);
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : EQUIPMENT_ERROR_MESSAGES.UNEXPECTED_ERROR;
        setSnackbarError(msg);
        setIsSnackbarOpen(true);
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    [items, validateAll]
  );

  /**
   * Clears all unsaved / transient facility and equipment state.
   * Called when navigating from step 2 back to step 1, and before starting a
   * new facility edit, so the form never shows stale data.
   *
   * Keeps:
   *  - Newly-created facilities that were submitted to the API
   *    (tempFacilityId !== null AND isEditMode is falsy)
   *  - Equipment within those facilities that was saved (serverEquipmentId set)
   *
   * Discards:
   *  - Facilities where tempFacilityId === null (never hit the API)
   *  - Edit-mode items (isEditMode === true) — these are transient; the user
   *    will start a fresh edit on re-entry, so keeping them would cause
   *    loadFacilityForEditing to append a duplicate form instead of replacing.
   *  - Equipment slots without a serverEquipmentId within kept facilities
   *    (replaces them with a single fresh empty slot)
   *
   * If nothing was ever submitted, resets to one empty facility form.
   */
  const resetUnsavedEntries = useCallback(() => {
    setItems((prev) => {
      // Edit-mode items (loaded from API for editing) are transient — they must
      // not survive a step 2 → step 1 navigation, because on re-entry the user
      // will start a fresh edit. Keeping them causes loadFacilityForEditing to
      // append a duplicate instead of replacing the first slot.
      const submitted = prev.filter(
        (item) => item.tempFacilityId !== null && !item.isEditMode
      );
      if (submitted.length === 0) {
        return [createEmptyFacilityItem()];
      }
      return submitted.map((item) => {
        const savedEquipment = item.equipment.filter(
          (e) => e.serverEquipmentId !== undefined
        );
        return {
          ...item,
          errors: createEmptyFacilityErrors(),
          // If equipment was already saved to the API, show the list (no pending
          // form slot). The user can click "Add Equipment" to open a new form.
          // If nothing was saved yet, keep one empty form so there is always
          // at least one slot to fill.
          equipment:
            savedEquipment.length > 0
              ? savedEquipment
              : [createEmptyEquipmentSubItem()],
        };
      });
    });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setIsSnackbarOpen(false);
    setSnackbarError('');
  }, []);

  const handleCloseSuccessSnackbar = useCallback(() => {
    setIsSuccessSnackbarOpen(false);
    setSuccessMessage('');
  }, []);

  const handleCloseEquipmentSuccessSnackbar = useCallback(() => {
    setIsEquipmentSuccessSnackbarOpen(false);
  }, []);

  const handleCloseEquipmentEditSuccessSnackbar = useCallback(() => {
    setIsEquipmentEditSuccessSnackbarOpen(false);
  }, []);

  const getFacilityStatus = useCallback(
    (facilityId: string): 'active' | 'inactive' | 'cascaded' | undefined =>
      items.find((item) => item.entry.id === facilityId)?.facilityStatus,
    [items]
  );

  const setFacilityItemStatus = useCallback(
    (facilityId: string, status: 'active' | 'inactive' | 'cascaded') => {
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === facilityId
            ? { ...item, facilityStatus: status }
            : item
        )
      );
    },
    []
  );

  const hasInProgressForms = items.some((item) => {
    const facilityDirty =
      item.entry.name !== '' ||
      item.entry.nip !== '' ||
      item.entry.phone !== '' ||
      item.entry.address1 !== '' ||
      item.entry.city !== '' ||
      item.entry.state !== '';
    const equipmentDirty = item.equipment.some(
      (e) =>
        e.entry.name !== '' ||
        e.entry.description !== '' ||
        e.entry.imageFile !== undefined ||
        e.entry.docFiles.length > 0 ||
        Object.keys(e.uploadProgress).length > 0
    );
    return facilityDirty || equipmentDirty;
  });

  return {
    facilityList,
    facilityErrorsList,
    isSubmitting,
    isFacilitySubmitting,
    isLoading,
    snackbarError,
    isSnackbarOpen,
    isSuccessSnackbarOpen,
    successMessage,
    isEquipmentSuccessSnackbarOpen,
    addFacilityEntry,
    loadFacilityForEditing,
    updateFacilityField,
    handleFacilityNipChange,
    handleFacilityPhoneChange,
    addEquipmentEntry,
    removeEquipmentEntry,
    resetFacilityFields,
    exitEditMode,
    resetEquipmentEntry,
    updateEquipmentField,
    updateEquipmentCategory,
    updateEquipmentDateOfProduction,
    getEquipmentList,
    getEquipmentErrors,
    getEquipmentConflicts,
    clearEquipmentConflicts,
    getUploadProgress,
    getFailedUploadFiles,
    getEquipmentServerIds,
    handleEquipmentImageChange,
    handleEquipmentImageError,
    handleEquipmentDocsChange,
    handleEquipmentDocsError,
    retryEquipmentUpload,
    validateEquipmentEntry,
    submitEquipmentEntry,
    validateAll,
    submitFacilityOnly,
    submit,
    handleCloseSnackbar,
    handleCloseSuccessSnackbar,
    handleCloseEquipmentSuccessSnackbar,
    savedFacilityCount,
    hasInProgressForms,
    getTempFacilityId,
    getIsEditMode,
    getFacilityStatus,
    setFacilityItemStatus,
    resetUnsavedEntries,
    loadingFacilityId,
    getEditingEquipment,
    startEquipmentEdit,
    cancelEquipmentEdit,
    updateEditEquipmentField,
    updateEditEquipmentCategory,
    updateEditEquipmentDateOfProduction,
    handleEditEquipmentImageChange,
    handleEditEquipmentImageError,
    handleEditEquipmentDocsChange,
    handleEditEquipmentDocsError,
    handleEditExistingImageDelete,
    handleEditExistingDocDelete,
    retryUploadSingleFile,
    submitEquipmentEdit,
    isEquipmentEditSuccessSnackbarOpen,
    handleCloseEquipmentEditSuccessSnackbar,
    equipmentEditCount,
  };
};
